import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Api } from '../lib/api.js';
import { useWallet, useApi } from '../lib/wallet.jsx';
import { Button } from '../components/ui.jsx';
import { EARN_STEPS } from '../components/Journey.jsx';

/**
 * Home — the single hub.
 *
 * One connected flow rail is the centrepiece: the daily check-in is the first
 * node, and it flows straight into the three earn steps — Map me, Deploy agent,
 * Generate workflow. The check-in is no longer a separate card floating above
 * the journey; it is the start of the same path, so a returning user's daily
 * tap and the high-value on-chain actions read as one habit loop.
 *
 * All icons are custom monoline pictograms (currentColor stroke, no emoji),
 * matching the Deploy screen's icon language.
 */

// ---- Pictograms: monoline, currentColor, no emoji. ----
const GLYPH = {
  // Flame — the streak marker.
  flame: (
    <path d="M12 3 C13 6 16 7.5 16 12 A4 4 0 0 1 8 12 C8 9.5 9 8 10 7 C10.5 9 12 9 12 9 C12 6.5 11 5 12 3 Z" />
  ),
  // Map me — a node-and-links glyph.
  map: (
    <>
      <circle cx="6" cy="7" r="2" />
      <circle cx="18" cy="6" r="2" />
      <circle cx="12" cy="17" r="2" />
      <path d="M7.7 8.5 L11 15.2 M16.6 7.6 L12.8 15.4 M8 7 H16" />
    </>
  ),
  // Deploy agent — a launch/hexagon-with-spark glyph.
  deploy: (
    <>
      <path d="M12 3 L20 7.5 V16.5 L12 21 L4 16.5 V7.5 Z" />
      <path d="M12 8 V16 M8.5 12 H15.5" />
    </>
  ),
  // Generate workflow — connected schedule/flow glyph.
  workflow: (
    <>
      <rect x="3.5" y="5" width="6" height="4" rx="1" />
      <rect x="14.5" y="15" width="6" height="4" rx="1" />
      <path d="M9.5 7 H13 A2 2 0 0 1 15 9 V15" />
    </>
  ),
  check: <path d="M5 12.5 L10 17.5 L19 6.5" />,
  arrow: <path d="M4 12 H19 M13 6 L19 12 L13 18" />,
};

function Glyph({ id, className = 'glyph' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {GLYPH[id] ?? null}
    </svg>
  );
}

const QUICK_EXAMPLES = [
  'Summarise the latest news on humanoid robots',
  'Draft a short, friendly outreach email to an investor',
  'What are the top AI stories this week?',
];

const FOCUS = [
  ['research', 'Research & news', 'Find current information on the web and report what is actually happening, with sources.'],
  ['writing', 'Writing & drafting', 'Draft and polish written material such as emails, posts and documents.'],
  ['coding', 'Coding help', 'Help with code: explain, write and debug, with working examples.'],
  ['markets', 'Markets & crypto', 'Track markets, tokens and crypto, and explain moves and numbers clearly.'],
  ['planning', 'Planning & organising', 'Break goals into concrete steps, plans and checklists.'],
  ['analysis', 'Summarise & analyse', 'Digest long or messy input into clear, structured takeaways.'],
];

const STYLE = [
  ['short', 'Short & direct', 'Answer as briefly as possible. Lead with the point. No filler.'],
  ['detailed', 'Detailed & thorough', 'Give complete, well-structured answers that cover the important angles.'],
  ['plain', 'Plain English', 'Explain simply, assume no jargon, define any necessary terms.'],
  ['formal', 'Professional', 'Keep a professional, business-appropriate tone.'],
];

// Streak milestones. Hitting one is a visible win; the rail always shows the
// next target and how far off it is.
const MILESTONES = [3, 7, 14, 30];

const EARN_GLYPHS = { '/map': 'map', '/deploy': 'deploy', '/build': 'workflow' };

/**
 * The unified flow rail: daily check-in as node 0, then the three earn steps.
 * One connected path so the daily tap and the high-value actions are a single
 * loop rather than two separate widgets.
 */
function FlowRail({ pushToast }) {
  const navigate = useNavigate();
  const { sessionReady, isAuthenticated, sendIntent } = useWallet();
  const { data, reload } = useApi(() => Api.dashboard(), [sessionReady], { skip: !sessionReady });
  const [busy, setBusy] = useState(false);

  // Daily check-in is wallet-scoped: no passport required, fires from tap one.
  const walletEngagement = data?.walletEngagement ?? null;

  const streak = walletEngagement ? Number(walletEngagement.currentStreak) : 0;
  const points = walletEngagement ? walletEngagement.points : '0';
  const checkedInToday = Boolean(walletEngagement?.checkedInToday);
  const mapCount = walletEngagement ? Number(walletEngagement.mapCount ?? 0) : 0;
  const deployCount = walletEngagement ? Number(walletEngagement.deployCount ?? 0) : 0;
  const workflowCount = walletEngagement ? Number(walletEngagement.workflowCount ?? 0) : 0;

  // Next check-in preview, read straight off chain via the dashboard:
  // the award the next tap pays and the streak it produces.
  const nextCheckIn = walletEngagement?.nextCheckIn ?? null;
  const nextAward = nextCheckIn && nextCheckIn.award != null ? Number(nextCheckIn.award) : null;
  const streakAfter = nextCheckIn && nextCheckIn.streakAfter != null ? Number(nextCheckIn.streakAfter) : streak + 1;

  // The single box the user should touch next. Drives the highlight so the
  // page always points at exactly one action: check in first, then map,
  // then deploy, then workflow. Null once the loop is complete for today.
  const doneMap = { '/map': mapCount > 0, '/deploy': deployCount > 0, '/build': workflowCount > 0 };
  const nextStep = !isAuthenticated
    ? null
    : !checkedInToday
      ? 'checkin'
      : mapCount === 0
        ? '/map'
        : deployCount === 0
          ? '/deploy'
          : workflowCount === 0
            ? '/build'
            : null;

  const nextMilestone = MILESTONES.find((m) => m > streak) ?? null;
  const prevMilestone = [...MILESTONES].reverse().find((m) => m <= streak) ?? 0;
  const daysToNext = nextMilestone ? nextMilestone - streak : 0;
  const progressPct = nextMilestone
    ? Math.max(4, Math.round(((streak - prevMilestone) / (nextMilestone - prevMilestone)) * 100))
    : 100;

  const checkIn = async () => {
    if (busy || checkedInToday) return;
    setBusy(true);
    try {
      const { intent } = await Api.walletCheckInIntent();
      const r = await sendIntent({ intent, txType: 'wallet_check_in' });
      if (r.status !== 'success') throw new Error('The check-in transaction reverted');
      const earned = nextAward != null ? `+${nextAward} credit${nextAward === 1 ? '' : 's'}` : 'Credits earned';
      pushToast?.({
        tone: 'success',
        message: `Checked in. ${earned}. Streak is ${streakAfter} day${streakAfter === 1 ? '' : 's'}. Next up: map yourself.`,
      });
      // Write is confirmed, but the read RPC can lag the write node on opBNB.
      // One reload can read pre-check-in state and leave the highlight stuck on
      // "check in" instead of advancing to Map me. Reconcile with spaced retries
      // so the highlight reliably walks to the next box as the read catches up.
      reload();
      [1200, 2600, 4200, 6500].forEach((ms) => setTimeout(() => reload(), ms));
    } catch (err) {
      pushToast?.({ tone: 'danger', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flow">
      <div className="flow__intro">
        <span className="flow__kicker">Your daily loop</span>
        <h2 className="flow__title">Check in, then earn on chain</h2>
        <p className="flow__subtitle">
          One tap a day keeps your streak alive. From there, map yourself, deploy an agent, and put it on a workflow. Every node is a signed action on opBNB.
        </p>
      </div>

      <ol className="flow__rail">
        {/* Node 0 — daily check-in, fused into the same rail as the earn steps */}
        <li className="flow__node flow__node--checkin">
          <div className={`flow__card flow__card--checkin${nextStep === 'checkin' ? ' flow__card--next' : ''}`}>
            {nextStep === 'checkin' && <span className="flow__nexttag">Start here</span>}
            <div className="flow__cardhead">
              <span className="flow__glyphwrap flow__glyphwrap--flame"><Glyph id="flame" /></span>
              <div className="flow__streak">
                <span className="flow__streaknum">{streak}</span>
                <span className="flow__streaklabel">day streak</span>
              </div>
            </div>
            <span className="flow__label">Daily check-in</span>
            <span className="flow__blurb">
              {nextMilestone
                ? `${daysToNext} day${daysToNext === 1 ? '' : 's'} to your ${nextMilestone}-day mark`
                : 'Past every milestone. Keep it going.'}
            </span>
            <div className="flow__progress" aria-hidden="true">
              <span className="flow__progressfill" style={{ width: `${progressPct}%` }} />
            </div>

            <div className="flow__stats">
              <span className="flow__points">{points} credits</span>
              {!checkedInToday && nextAward != null && (
                <span className="flow__nextaward">Check in for +{nextAward}</span>
              )}
            </div>

            {isAuthenticated && !checkedInToday && (
              <span className="flow__loss flow__loss--build">
                Check in daily to grow your streak. Longer streaks pay more credits per tap.
              </span>
            )}

            <div className="flow__cta">
              {!isAuthenticated ? (
                <span className="flow__gate">Connect wallet to check in</span>
              ) : (
                <Button
                  variant="primary"
                  loading={busy}
                  onClick={checkIn}
                  disabled={checkedInToday}
                  title={checkedInToday ? 'Come back in 24 hours' : undefined}
                >
                  {checkedInToday ? (
                    <span className="flow__done"><Glyph id="check" className="glyph glyph--sm" /> Checked in</span>
                  ) : nextAward != null ? (
                    `Check in for +${nextAward}`
                  ) : (
                    'Check in'
                  )}
                </Button>
              )}
            </div>
          </div>
          <span className="flow__arrow" aria-hidden="true"><Glyph id="arrow" /></span>
        </li>

        {/* Earn steps — same rail, so the flow reads as one path */}
        {EARN_STEPS.map((s, i) => {
          const isNext = nextStep === s.path;
          const isDone = doneMap[s.path];
          return (
          <li key={s.path} className="flow__node">
            <button
              type="button"
              className={`flow__card${isNext ? ' flow__card--next' : ''}${isDone ? ' flow__card--complete' : ''}`}
              onClick={() => navigate(s.path)}
              aria-current={isNext ? 'step' : undefined}
            >
              {isNext && <span className="flow__nexttag">Do this next</span>}
              {isDone && <span className="flow__donetag" aria-label="Completed"><Glyph id="check" className="glyph glyph--sm" /></span>}
              <div className="flow__cardhead">
                <span className="flow__glyphwrap"><Glyph id={EARN_GLYPHS[s.path]} /></span>
                <span className="flow__num" aria-hidden="true">{i + 1}</span>
              </div>
              <span className="flow__label">{s.label}</span>
              <span className="flow__blurb">{s.blurb}</span>
            </button>
            {i < EARN_STEPS.length - 1 && (
              <span className="flow__arrow" aria-hidden="true"><Glyph id="arrow" /></span>
            )}
          </li>
          );
        })}
      </ol>
    </section>
  );
}

export function Home({ pushToast }) {
  const navigate = useNavigate();
  const { sessionReady } = useWallet();

  const [task, setTask] = useState('');
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [focus, setFocus] = useState([]);
  const [style, setStyle] = useState('short');
  const [about, setAbout] = useState('');
  const [building, setBuilding] = useState(false);

  const quickStart = async (explicit) => {
    const message = (typeof explicit === 'string' ? explicit : task).trim();
    if (!message || busy || !sessionReady) return;
    setBusy(true);
    try {
      let agentId = null;
      try {
        const { agents } = await Api.agents();
        agentId = agents.find((a) => a.status !== 'archived')?.id ?? null;
      } catch {
        /* no session yet: fall through to creation */
      }
      if (!agentId) {
        const { agent } = await Api.createAgent({
          name: 'Assistant',
          role: 'A general-purpose assistant inside Cortis, an app where you ask an AI agent to do a task and it searches the web, reads sources, and writes you a clear answer with citations. Answer clearly and directly, use the web for anything current, and cite sources.',
        });
        agentId = agent.id;
      }
      navigate(`/agents/${agentId}`, { state: { initialTask: message } });
    } catch (err) {
      pushToast?.({ tone: 'danger', message: err.message });
      setBusy(false);
    }
  };

  const toggleFocus = (key) =>
    setFocus((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const composeBrief = () => {
    const parts = [];
    const focusLines = FOCUS.filter(([k]) => focus.includes(k)).map(([, , sentence]) => sentence);
    if (focusLines.length) {
      parts.push(`This agent is for: ${focusLines.join(' ')}`);
    } else {
      parts.push('This agent is a general-purpose assistant.');
    }
    const styleLine = STYLE.find(([k]) => k === style)?.[2];
    if (styleLine) parts.push(`Style: ${styleLine}`);
    if (about.trim()) parts.push(`About the person you are helping: ${about.trim()}`);
    parts.push('When a task needs current facts, use the web and cite source URLs.');
    return parts.join('\n');
  };

  const buildAgent = async () => {
    if (building || !sessionReady) return;
    setBuilding(true);
    try {
      const finalName = name.trim() || 'My agent';
      const { agent } = await Api.createAgent({ name: finalName.slice(0, 120), role: composeBrief() });
      pushToast?.({ tone: 'success', message: `${finalName} is ready.` });
      navigate(`/agents/${agent.id}`);
    } catch (err) {
      pushToast?.({ tone: 'danger', message: err.message });
      setBuilding(false);
    }
  };

  return (
    <div className="home">
      {/* Unified flow: check-in fused into the map -> deploy -> workflow path */}
      <FlowRail pushToast={pushToast} />

      {/* Path 1 — ask once */}
      <section className="home__hero">
        <span className="home__badge">Verifiable AI agents</span>
        <h1 className="home__title">
          Get real work done <span className="home__title-accent">in seconds</span>
        </h1>
        <p className="home__subtitle">
          Describe a task in plain words. Cortis puts an AI agent on it, searches live sources, and writes you a clear, cited answer.
        </p>

        <div className="home__box">
          <label className="sr-only" htmlFor="home-task">Describe your task</label>
          <textarea
            id="home-task"
            className="home__input"
            rows={3}
            value={task}
            placeholder="e.g. Summarise the latest news on humanoid robots"
            onChange={(e) => setTask(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                quickStart();
              }
            }}
          />
          <Button variant="primary" loading={busy} onClick={() => quickStart()} disabled={!task.trim()}>
            Start
          </Button>
        </div>

        <div className="home__examples">
          <span className="home__examples-label">Try one:</span>
          {QUICK_EXAMPLES.map((ex) => (
            <button key={ex} type="button" className="home__chip" onClick={() => quickStart(ex)} disabled={busy}>
              {ex}
            </button>
          ))}
        </div>
      </section>

      {/* Path 2 — build a custom agent by clicking boxes */}
      <section className="builder">
        <div className="builder__head">
          <h2 className="builder__title">Or build your own agent</h2>
          <p className="builder__subtitle">
            Click what it should do and how it should talk. Cortis turns your choices into an agent that works your way.
          </p>
        </div>

        <div className="builder__group">
          <span className="builder__label">What should it help with?</span>
          <div className="builder__options">
            {FOCUS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`option${focus.includes(key) ? ' option--on' : ''}`}
                aria-pressed={focus.includes(key)}
                onClick={() => toggleFocus(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="builder__group">
          <span className="builder__label">How should it talk to you?</span>
          <div className="builder__options">
            {STYLE.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`option${style === key ? ' option--on' : ''}`}
                aria-pressed={style === key}
                onClick={() => setStyle(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="builder__group">
          <label className="builder__label" htmlFor="builder-about">
            Anything it should know about you? <span className="builder__optional">(optional)</span>
          </label>
          <textarea
            id="builder-about"
            className="builder__about"
            rows={2}
            value={about}
            placeholder="e.g. I run a robotics startup in Dubai. Keep answers focused on business impact."
            onChange={(e) => setAbout(e.target.value)}
          />
        </div>

        <div className="builder__group builder__group--name">
          <label className="builder__label" htmlFor="builder-name">Give it a name</label>
          <div className="builder__namerow">
            <input
              id="builder-name"
              className="builder__name"
              value={name}
              maxLength={120}
              placeholder="My agent"
              onChange={(e) => setName(e.target.value)}
            />
            <Button variant="primary" loading={building} onClick={buildAgent}>
              Create my agent
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
